import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { lazy, Suspense, useEffect, useRef } from "react";
import { ErrorBoundary } from "@/components/common";
import { logInfo } from "@/lib/logger";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Toaster } from "@strivelabs/ui-core";
import { ToastHost } from "@strivelabs/ui-core/v2";
import { useOrganization, useOrganizationList, useUser } from "@clerk/clerk-react";
import { STRIVELABS_USERS } from "@/constants";
import { ChartsDemo } from "./components/demo/ChartsDemo";
import { useTenantDetails } from "@/hooks/useConnections";
import { useAppStore } from "@/store/appStore";
import { ContainerDemo } from "./components/demo/ContainerDemo";
import { InsightsDemo } from "./components/demo/InsightsDemo";
import { ChatProvider } from "@/contexts/ChatContext";
import { ThemeProvider } from "@/hooks/useTheme";
import '@/lib/themes'; // Register all themes on app load

// Lazy loading pages for better performance
const SignIn = lazy(() => import("@/pages/SignIn"));
const SignUp = lazy(() => import("@/pages/SignUp"));
const HomeOrWorkforcePage = lazy(() => import("@/pages/HomeOrWorkforcePage"));
const MyWorkforce = lazy(() => import("@/pages/MyWorkforce"));
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const Experiments = lazy(() => import("@/pages/Experiments"));
const SettingsLayout = lazy(() => import("@/pages/settings/SettingsLayout"));
const SettingsIndexRedirect = lazy(() => import("@/pages/settings/SettingsIndexRedirect"));
const SettingsProfile = lazy(() => import("@/pages/settings/pages/SettingsProfile"));
const SettingsConnections = lazy(() => import("@/pages/settings/pages/SettingsConnections"));
const SettingsNotifications = lazy(() => import("@/pages/settings/pages/SettingsNotifications"));
const SettingsOrganization = lazy(() => import("@/pages/settings/pages/SettingsOrganization"));
const SettingsMembers = lazy(() => import("@/pages/settings/pages/SettingsMembers"));
const AgentLayout = lazy(() => import("@/pages/AgentLayout"));
const InsightReport = lazy(() => import("@/pages/InsightReport"));
const AnswerEngineOptimization = lazy(() => import("@/agents/answer-engine-optimization/AnswerEngineOptimization"))
const Reports = lazy(() => import("@/pages/Reports"));
const Routines = lazy(() => import("@/pages/Routines"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const RoutineDetail = lazy(() => import("@/pages/RoutineDetail"));
const RoutineAlertHandler = lazy(() => import("@/pages/RoutineAlertHandler"));
const KnowledgeHubLayout = lazy(() => import("@/pages/KnowledgeHub/KnowledgeHubLayout"));
const KnowledgeHubHome = lazy(() => import("@/pages/KnowledgeHub/KnowledgeHubHome"));
const KnowledgeHubFile = lazy(() => import("@/pages/KnowledgeHub/KnowledgeHubFile"));
const SkillsLayout = lazy(() => import("@/pages/Skills/SkillsLayout"));
const SkillsHome = lazy(() => import("@/pages/Skills/SkillsHome"));
const SkillView = lazy(() => import("@/pages/Skills/SkillView"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const BlockKitPlayground = lazy(() => import("@/pages/BlockKitPlayground"));
const BlogPostStrategizerLayout = lazy(() => import("@/agents/blog-post-content-strategizer/BlogPostStrategizerLayout"));
const BlogPostRefresherLayout = lazy(() => import("@/agents/blog-post-content-refresher/BlogPostRefresherLayout"));
const SeoAuditLayout = lazy(() => import("@/agents/seo-audit/SeoAuditLayout"));
const NegativeKeywordAnalyzerLayout = lazy(() => import("@/agents/negative-keyword-analyzer/NegativeKeywordAnalyzerLayout"));
const PdfRenderer = lazy(() => import("@/components/pdf/PdfRenderer"));

// Onboarding flow components
const OnboardingLayout = lazy(() => import("@/pages/onboarding/OnboardingLayout").then(m => ({ default: m.OnboardingLayout })));
const CompanyInfoStep = lazy(() => import("@/pages/onboarding/CompanyInfoStep").then(m => ({ default: m.CompanyInfoStep })));
const BuildingProfileStep = lazy(() => import("@/pages/onboarding/BuildingProfileStep").then(m => ({ default: m.BuildingProfileStep })));
const BrandStep = lazy(() => import("@/pages/onboarding/BrandStep").then(m => ({ default: m.BrandStep })));
const CompetitorsStep = lazy(() => import("@/pages/onboarding/CompetitorsStep").then(m => ({ default: m.CompetitorsStep })));
const ConnectDataStep = lazy(() => import("@/pages/onboarding/ConnectDataStep").then(m => ({ default: m.ConnectDataStep })));

// Post-onboarding setup (live build) page
const SetupLayout = lazy(() => import("@/pages/onboardingSetup/SetupLayout"));
const SetupHome = lazy(() => import("@/pages/onboardingSetup/SetupHome"));

// @demo: uncomment routes in demoRoutes.tsx to include them in the build
import { DemoRoutes } from "@/demoRoutes";

// Redirect that preserves query parameters (redirect_url, etc.)
function NavigatePreservingSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

// Legacy Report Studio deep links — the studio is now the global chat's
// report canvas slot. Old bookmarks land on the report viewer, whose
// "Customize in Studio" action opens a bound chat thread.
function NavigateReportStudioToViewer() {
  const { insightId } = useParams<{ insightId: string }>();
  return <Navigate to={`/ui/reports/${insightId}`} replace />;
}

// Loading component for suspense fallback
const Loading = () => {
  logInfo('Loading component rendered', { component: 'App Loading' });
  return (
    <div role="status" className="flex items-center justify-center h-screen">
      <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
      <span className="sr-only">Loading…</span>
    </div>
  );
};

// Layout component for protected routes
const ProtectedLayout = () => (
  <ErrorBoundary>
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  </ErrorBoundary>
);

// Layout for internal-only routes — redirects non-StriveLabs users to home
const InternalRoute = () => {
  const { user, isLoaded } = useUser();
  if (!isLoaded) return null;
  const userEmail = user?.primaryEmailAddress?.emailAddress;
  if (userEmail && !STRIVELABS_USERS.includes(userEmail)) {
    return <Navigate to="/ui" replace />;
  }
  return <Outlet />;
};

export default function App() {
  logInfo('App component rendered');

  // setting identity to identify the user in analytics
  useAnalytics();

  // Auto-select and set the first available organization as active
  const { isLoaded: isActiveOrgDetailsLoaded, organization } = useOrganization();

  // Set tenant details in global store — only fetch when an org is active
  // (first-time users going through onboarding don't have an org/tenant yet)
  const { data: tenantDetails } = useTenantDetails({ enabled: Boolean(organization) });
  useEffect(() => {
    if (tenantDetails) {
      useAppStore.getState().setTenantDetails(tenantDetails);
      logInfo('App: Tenant details set in global store', { tenantId: tenantDetails.tenant.id });
    }
  }, [tenantDetails]);
  const { isLoaded: isOrgListLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const orgInitRef = useRef(false);

  useEffect(() => {
    // Avoid repeated execution
    if (orgInitRef.current) return;

    // If active org is already available, do nothing
    if (isActiveOrgDetailsLoaded && organization) {
      orgInitRef.current = true;
      return;
    }

    // Wait until both org context and membership list are loaded and data is fetched
    if (!isActiveOrgDetailsLoaded || !isOrgListLoaded || userMemberships?.isLoading) return;

    // If a local org is configured (development only), prefer it; otherwise pick the first membership
    const localClerkOrgId =
      import.meta.env.MODE === 'development' ? import.meta.env.VITE_LOCAL_CLERK_ORG_ID : undefined;
    const targetMembership =
      (localClerkOrgId &&
        userMemberships?.data?.find((m) => m.organization.id === localClerkOrgId)) ||
      userMemberships?.data?.[0];
    if (targetMembership?.organization) {
      setActive({ organization: targetMembership.organization.id })
        .then(() => {
          orgInitRef.current = true;
          logInfo('App: Set first organization as active', {
            orgId: targetMembership.organization.id,
          });
        })
        .catch(() => {
          // Non-blocking: just mark as initialized to prevent loops
          orgInitRef.current = true;
        });
    } else if (!userMemberships?.isFetching) {
      // No orgs available and not fetching more; mark as initialized
      orgInitRef.current = true;
    }
  }, [isActiveOrgDetailsLoaded, isOrgListLoaded, organization, userMemberships, setActive]);

  return (
    <ThemeProvider>
    <ChatProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <Routes>
              {/* Protected Routes */}
              <Route element={<ProtectedLayout />}>
                <Route element={<AppShell />}>
                <Route path="/ui" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/chat" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/chat/:executionId" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/routine-alert/:cardId" element={<RoutineAlertHandler />} />
                <Route path="/ui/my-workforce" element={<MyWorkforce />} />
                <Route element={<InternalRoute />}>
                  <Route path="/ui/marketplace" element={<Marketplace />} />
                  <Route path="/ui/experiments" element={<Experiments />} />
                </Route>
                <Route path="/ui/settings" element={<SettingsLayout />}>
                  <Route index element={<SettingsIndexRedirect />} />
                  <Route path="profile" element={<SettingsProfile />} />
                  <Route path="connections" element={<SettingsConnections />} />
                  <Route path="notifications" element={<SettingsNotifications />} />
                  <Route path="organization" element={<SettingsOrganization />} />
                  <Route path="members" element={<SettingsMembers />} />
                </Route>
                <Route path="/ui/routines" element={<Routines />} />
                <Route path="/ui/tasks" element={<Tasks />} />
                <Route path="/ui/routines/:routineId" element={<RoutineDetail />} />
                <Route path="/ui/skills" element={<SkillsLayout />}>
                  <Route index element={<SkillsHome />} />
                  <Route path=":skillShortName" element={<SkillView />} />
                </Route>
                <Route path="/ui/glossary" element={<Glossary />} />
                <Route path="/ui/reports" element={<Reports />} />
                <Route path="/ui/reports/:insightId" element={<InsightReport />} />
                <Route path="/ui/knowledge-hub" element={<KnowledgeHubLayout />}>
                  <Route index element={<KnowledgeHubHome />} />
                  <Route path=":knowledgeHubId" element={<KnowledgeHubFile />} />
                </Route>
                <Route path="/ui/onboarding/setup" element={<SetupLayout />}>
                  <Route index element={<SetupHome />} />
                </Route>
                <Route path="/ui/agent/aeo" element={<AnswerEngineOptimization />} />
                <Route path="/ui/agent/blog-post-content-strategizer/*" element={<BlogPostStrategizerLayout />} />
                <Route path="/ui/agent/blog-post-content-refresher/*" element={<BlogPostRefresherLayout />} />
                <Route path="/ui/agent/seo-audit/*" element={<SeoAuditLayout />} />
                <Route path="/ui/agent/negative-keyword-analyzer/*" element={<NegativeKeywordAnalyzerLayout />} />

                {/* @demo: uncomment routes in demoRoutes.tsx to activate them */}
                {DemoRoutes}

                <Route path="/ui/agent/:agentId" element={<AgentLayout />} />
                </Route>

                {/* Pure redirects — kept outside AppShell so they resolve
                    without flashing the sidebar and header for a frame. */}
                <Route path="/ui/report-studio/:insightId" element={<NavigateReportStudioToViewer />} />
                <Route path="/ui/report-studio/:insightId/:executionId" element={<NavigateReportStudioToViewer />} />
                <Route path="/ui/agent/answer-engine-optimization" element={<NavigatePreservingSearch to="/ui/agent/aeo" />} />
                <Route path="/ui/agent/aeo-v2" element={<NavigatePreservingSearch to="/ui/agent/aeo" />} />
                <Route path="/ui/agent/seo-audit-v2" element={<NavigatePreservingSearch to="/ui/agent/seo-audit" />} />
              </Route>

              {/* Public/Special Routes with ErrorBoundary only */}
              <Route path="/ui/charts" element={
                <ErrorBoundary>
                  <ChartsDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/slots" element={
                <ErrorBoundary>
                  <ContainerDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/insights" element={
                <ErrorBoundary>
                  <InsightsDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/block-kit-playground" element={
                <ErrorBoundary>
                  <BlockKitPlayground />
                </ErrorBoundary>
              } />
              <Route path="/ui/pdf-renderer" element={
                <ErrorBoundary>
                  <PdfRenderer />
                </ErrorBoundary>
              } />
              <Route path="/ui/onboarding" element={
                <ErrorBoundary>
                  <OnboardingLayout />
                </ErrorBoundary>
              }>
                <Route index element={<NavigatePreservingSearch to="company-info" />} />
                <Route path="company-info" element={<CompanyInfoStep />} />
                <Route path="building" element={<BuildingProfileStep />} />
                <Route path="brand" element={<BrandStep />} />
                <Route path="competitors" element={<CompetitorsStep />} />
                <Route path="connect" element={<ConnectDataStep />} />
              </Route>
              <Route path="/ui/sign-in" element={
                <ErrorBoundary>
                  <SignIn />
                </ErrorBoundary>
              } />
              <Route path="/ui/sign-up" element={
                <ErrorBoundary>
                  <SignUp />
                </ErrorBoundary>
              } />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <Toaster />

        <ToastHost />
      </BrowserRouter>
    </ChatProvider>
    </ThemeProvider>
  );
}
